import { useMemo } from 'react'
import {
  Message,
  IMessage,
  RawTextMessage,
  RawImageMessage,
  RawVideoMessage,
  RawFileMessage,
  RawWappMessage,
  RawAudioMessage,
  CallMessageAttachment,
  MessageSender,
} from '@weblite/ts-action/message'
import { getWappTheme } from '$common/wapp-bar-theme'
import { MessagePreviewMessageDescriptorTypography } from '$common/messsage-preview'
import { useWapp } from '$logic/wapps/react-hook'
import { useEventMessageText } from '$logic/message/react-hook'
import { getCallMessageStatusText } from '$logic/call'
import { t } from '$i18n'

interface MessageDescriptorComponentProps<M> {
  message: M
}

function TextMessage({
  message: { body },
}: MessageDescriptorComponentProps<RawTextMessage>) {
  return (
    <MessagePreviewMessageDescriptorTypography color="black">
      {body}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function VoiceMessage() {
  return (
    <MessagePreviewMessageDescriptorTypography color="neutral500">
      {t('message.VOICE')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function ImageMessage({
  message,
}: MessageDescriptorComponentProps<RawImageMessage>) {
  const caption = message.body

  return (
    <MessagePreviewMessageDescriptorTypography
      color={caption ? 'black' : 'neutral500'}
    >
      {caption || t('message.IMAGE')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function VideoMessage({
  message,
}: MessageDescriptorComponentProps<RawVideoMessage>) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const caption = message.body

  return (
    <MessagePreviewMessageDescriptorTypography
      color={caption ? 'black' : 'neutral500'}
    >
      {caption || t('message.VIDEO')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function FileMessage({
  message,
}: MessageDescriptorComponentProps<RawFileMessage>) {
  const caption = message.body
  const fileName = message.attachment.filename

  return (
    <MessagePreviewMessageDescriptorTypography
      color={caption ? 'black' : 'neutral500'}
    >
      {caption || fileName}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function AudioMessage({
  message,
}: MessageDescriptorComponentProps<RawAudioMessage>) {
  const fileName = message.attachment.filename

  return (
    <MessagePreviewMessageDescriptorTypography color="neutral500">
      {fileName}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function ContactMessage() {
  return (
    <MessagePreviewMessageDescriptorTypography color="neutral500">
      {t('message.CONTACT')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function CallMessage({ message }: MessageDescriptorComponentProps<IMessage>) {
  const { id: senderId } = message.sender as MessageSender
  const { status } = message.attachment as CallMessageAttachment

  const statusText = useMemo(() => getCallMessageStatusText(status, senderId), [
    senderId,
    status,
  ])

  return (
    <MessagePreviewMessageDescriptorTypography color="neutral500">
      {statusText}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function WappMessage({
  message,
}: MessageDescriptorComponentProps<RawWappMessage>) {
  const { wappId } = message.attachment
  useWapp(wappId)
  const wappTheme = getWappTheme(wappId)

  return (
    <MessagePreviewMessageDescriptorTypography color="neutral500">
      {wappTheme.name}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function EventMessage({ message }: { message: IMessage }) {
  const { eventText } = useEventMessageText(message)

  return (
    <MessagePreviewMessageDescriptorTypography color="neutral500">
      {eventText}
    </MessagePreviewMessageDescriptorTypography>
  )
}

function DeletedMessage() {
  return (
    <MessagePreviewMessageDescriptorTypography color="neutral500">
      {t('message.deleted')}
    </MessagePreviewMessageDescriptorTypography>
  )
}

const messageDescriptorComponentMap = {
  TEXT: TextMessage,
  VOICE: VoiceMessage,
  IMAGE: ImageMessage,
  FILE: FileMessage,
  WAPP: WappMessage,
  AUDIO: AudioMessage,
  VIDEO: VideoMessage,
  CONTACT: ContactMessage,
  CALL: CallMessage,
}

export function MessageDescriptor({ message }: { message: Message }) {
  if (message.patches?.deleted) return <DeletedMessage />

  if (message.type === 'EVENT')
    return <EventMessage message={message as IMessage} />

  const MessageDescriptorComponent = messageDescriptorComponentMap[message.type]
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return <MessageDescriptorComponent message={message} />
}
